import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { callbackUrl } from '../../api/util';
import AuthenticationErrorMessage from '../../components/AuthenticationErrorMessage/AuthenticationErrorMessage';
import { PARAM_AUTH_REQ_ID, PARAM_CODE, PARAM_STATE } from '../../components/AuthenticationWrapper';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import GovernmentIdField from '../../components/GovernmentIdField/GovernmentIdField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { extractTokenClaim } from '../../helpers/jwt';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useSaveDraftConsent from '../../hooks/useSaveDraftConsent';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { getAccessToken, initAuthentication, selectValidAccessToken } from '../../store/authentication';
import { WizardPageAction } from '../../store/wizard/wizard';

const JWT_CLAIM_SBL_PUBLIC_ID = 'publicId';
const CO_APPLICANT_SBL_REFERENCE_ID = 'coapplicantSblReferenceId';
const CO_APPLICANT_GOVERNMENT_ID = 'coapplicantGovernmentId';

const CoApplicantDraftRequestSBLConsentPage = () => {
    const { countryCode, languageCode } = useSettings();
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const sblConsentFieldState = useAppSelector(selectFieldStateByName)(CO_APPLICANT_SBL_REFERENCE_ID);
    const coapplicantGovernmentIdFieldState = useAppSelector(selectFieldStateByName)(CO_APPLICANT_GOVERNMENT_ID);
    const { applicationId } = useAppSelector((state) => state.application);
    const [params, setParams] = useSearchParams();
    const accessToken = useAppSelector(selectValidAccessToken);
    const { currentPage, pageAction } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const validationErrors = useValidation([CO_APPLICANT_GOVERNMENT_ID]);
    const [error, setError] = useState<boolean>(false);
    const applicationReferenceNumber = useArnFromPath();
    const saveDraftConsent = useSaveDraftConsent();
    useSetBannerType('bannerOne');

    const dispatchGetAccessToken = () =>
        dispatch(
            getAccessToken({
                state: params.get(PARAM_STATE)!,
                code: params.get(PARAM_CODE)!,
            }),
        );

    useEffect(() => {
        if (pageAction === 'back' && sblConsentFieldState?.value) {
            defaultPagesAction(pageAction);
        }

        if (pageAction === 'next' && sblConsentFieldState?.value) {
            defaultPagesAction(pageAction);
        }
    }, [pageAction, sblConsentFieldState]);

    useEffect(() => {
        if (accessToken) {
            return;
        }

        if (params.get(PARAM_CODE) && params.get(PARAM_STATE)) {
            dispatchGetAccessToken();
            params.delete(PARAM_CODE);
            params.delete(PARAM_STATE);
            params.delete(PARAM_AUTH_REQ_ID);
            setParams(params);
        }
    }, [params, accessToken]);

    const saveConsent = useCallback(
        async (consentId: string, pageAction?: WizardPageAction) => {
            try {
                await saveDraftConsent(consentId, 'SBL');
                dispatch(addOrUpdateFormField({ fieldName: CO_APPLICANT_SBL_REFERENCE_ID, value: consentId }));
                if (pageAction) {
                    defaultPagesAction(pageAction);
                }
            } catch (error) {
                setError(true);
            }
        },
        [saveDraftConsent, dispatch, defaultPagesAction],
    );

    useEffect(() => {
        // We don't have a login so we can't set the consent or we have already given our consent
        if (!accessToken || sblConsentFieldState?.value) {
            return;
        }
        const sblPublicId = extractTokenClaim(accessToken, JWT_CLAIM_SBL_PUBLIC_ID);
        if (sblPublicId) {
            saveConsent(sblPublicId, pageAction);
        }
    }, [accessToken, applicationId, sblConsentFieldState, pageAction]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'consent') {
            if (validationErrors.length) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
            try {
                setError(false);
                await dispatch(
                    initAuthentication(coapplicantGovernmentIdFieldState?.value + '', {
                        scope: 'cl-sbl-NO',
                        redirectUrl: callbackUrl(
                            ConsumerLoanTargetPath.coapplicantDraft,
                            languageCode,
                            countryCode,
                            applicationReferenceNumber!,
                        ),
                    }),
                );
                await new Promise((resolve) => setTimeout(resolve, 5000));
            } catch (error) {
                setError(true);
            }
            return false;
        }
        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={3}
                numberOfPagesInSection={1}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.sbl')}
            />
            <Title text={translate('page.request-sbl.title')} />
            <Description text={translate('page.verify-income.description')} />
            <GovernmentIdField
                name="coapplicantGovernmentId"
                countrycode={countryCode}
                label={translate('field.applicant-government-id.label')}
            />
            {error && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <AuthenticationErrorMessage />
            <PagesFooter
                actions={['consent']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={3}
                numberOfPagesInSection={1}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.sbl')}
            />
        </>
    );
};

export default CoApplicantDraftRequestSBLConsentPage;
